import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

/**
 * Todas as telas carregadas sob demanda.
 *
 * O detalhe do contrato traz o painel de simulação, que é a maior tela do
 * sistema — e a maioria das visitas vai para o painel de pendências e nunca a
 * abre. Sem a divisão, todo mundo baixaria a simulação para ver a lista.
 */
const router = createRouter({
  history: createWebHistory(),

  // Voltar deve devolver a pessoa ao ponto em que ela estava na lista, e não ao
  // topo — quem clica no 40º contrato e volta não quer rolar de novo.
  scrollBehavior: (_para, _de, guardado) => guardado ?? { top: 0 },

  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { publica: true },
    },
    {
      path: '/',
      name: 'painel',
      component: () => import('@/pages/PainelPage.vue'),
    },
    {
      path: '/contratos',
      name: 'contratos',
      component: () => import('@/pages/ContratosPage.vue'),
    },
    {
      path: '/contratos/novo',
      name: 'contrato-novo',
      component: () => import('@/pages/ContratoFormPage.vue'),
      meta: { permissao: 'contratos.gerenciar' },
    },
    {
      path: '/contratos/:id(\\d+)/editar',
      name: 'contrato-editar',
      component: () => import('@/pages/ContratoFormPage.vue'),
      meta: { permissao: 'contratos.gerenciar' },
    },
    {
      path: '/contratos/:id(\\d+)',
      name: 'contrato',
      component: () => import('@/pages/ContratoPage.vue'),
    },
    {
      path: '/fornecedores',
      name: 'fornecedores',
      component: () => import('@/pages/FornecedoresPage.vue'),
    },
    {
      path: '/fornecedores/novo',
      name: 'fornecedor-novo',
      component: () => import('@/pages/FornecedorFormPage.vue'),
      meta: { permissao: 'fornecedores.gerenciar' },
    },
    {
      path: '/fornecedores/:id(\\d+)/editar',
      name: 'fornecedor-editar',
      component: () => import('@/pages/FornecedorFormPage.vue'),
      meta: { permissao: 'fornecedores.gerenciar' },
    },
    {
      path: '/fornecedores/:id(\\d+)',
      name: 'fornecedor',
      component: () => import('@/pages/FornecedorPage.vue'),
    },
    {
      path: '/usuarios',
      name: 'usuarios',
      component: () => import('@/pages/admin/UsuariosPage.vue'),
      meta: { permissao: 'usuarios.visualizar' },
    },
    {
      path: '/papeis',
      name: 'papeis',
      component: () => import('@/pages/admin/PapeisPage.vue'),
      meta: { permissao: 'papeis.gerenciar' },
    },
    {
      path: '/empresas',
      name: 'empresas',
      component: () => import('@/pages/EmpresasPage.vue'),
      meta: { permissao: 'empresas.visualizar' },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'nao-encontrado',
      component: () => import('@/pages/NaoEncontradaPage.vue'),
      meta: { publica: true },
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Uma única verificação por carregamento da página. Sem isso, cada navegação
  // dispararia um GET /me.
  if (!auth.carregado) {
    await auth.carregarUsuario()
  }

  if (to.meta.publica) {
    // Quem já está autenticado não precisa ver a tela de login.
    return to.name === 'login' && auth.user ? { name: 'painel' } : true
  }

  if (!auth.user) {
    // Guarda o destino para voltar a ele depois de entrar.
    return { name: 'login', query: { destino: to.fullPath } }
  }

  /*
   * A permissão da rota é conveniência, não autorização.
   *
   * Ela evita que o link colado leve a uma tela que só sabe mostrar 403 — quem
   * decide de verdade continua sendo o servidor, em cada requisição. Por isso o
   * desvio vai para o painel e não para uma tela de "acesso negado": a pessoa
   * não pediu aquilo, ela seguiu um link.
   */
  const permissao = to.meta.permissao as string | undefined

  if (permissao && !auth.pode(permissao)) {
    return { name: 'painel' }
  }

  return true
})

export default router
